import { useState, useEffect } from "react";
import { Star, BadgeCheck } from "lucide-react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const TestimonialSubmitPage = () => {
  const { memberId } = useParams();
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", role: "",
    relationship_type: "Client", linkedin_url: "", rating: 5,
    problem: "", solution: "", outcome: "",
    talent_partner_id: memberId || "",
  });
  const [tpInfo, setTpInfo] = useState(null); // {name, title, specialist} when memberId is valid
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testimonialId, setTestimonialId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // If the URL has a memberId, resolve the Talent Partner's name + title to show as banner
  useEffect(() => {
    if (!memberId) return;
    axios.get(`${API}/api/testimonials/share-info/${encodeURIComponent(memberId)}`)
      .then(({ data }) => setTpInfo(data))
      .catch(() => setTpInfo(null));
  }, [memberId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/testimonials/submit`, form);
      setTestimonialId(res.data.id);
      if (logoFile && res.data.id) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await axios.post(`${API}/api/testimonials/${res.data.id}/upload-logo`, fd);
      }
      setSubmitted(true);
      toast.success("Testimonial submitted successfully!");
    } catch {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 bg-[#FAF9F6]">
        <SEO title="Thank You - Testimonial Submitted" canonical="https://magnuskor.com/testimonial/submit" />
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <div className="w-16 h-16 bg-[#B8860B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Star className="text-[#B8860B]" size={28} fill="#B8860B" />
          </div>
          <h1 className="font-display text-2xl text-[#1A1A1A] mb-4" data-testid="testimonial-thank-you">Thank You!</h1>
          <p className="text-gray-500 mb-8">Your testimonial has been submitted and is pending review. Once approved, it will appear on our website.</p>
          <a href="/" className="text-[#B8860B] hover:underline text-sm">Back to Homepage</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO
        title="Share Your Experience - Submit a Testimonial"
        description="Share your experience working with Magnus Kor Talent Partners. Your feedback helps other founders and talent find the right advisory partner."
        canonical="https://magnuskor.com/testimonial/submit"
      />
      <div className="max-w-2xl mx-auto px-6 py-16">
        {tpInfo && (
          <div className="mb-8 border border-[#B8860B]/30 bg-[#B8860B]/5 rounded-lg p-4 flex items-start gap-3" data-testid="tp-credit-banner">
            <BadgeCheck className="text-[#B8860B] shrink-0 mt-0.5" size={18} />
            <div className="text-sm leading-relaxed">
              You're leaving a review for <span className="font-semibold text-[#1A1A1A]">{tpInfo.name}</span>
              <span className="text-gray-500"> · {tpInfo.title}{tpInfo.specialist ? ` · ${tpInfo.specialist}` : ""}</span>
              <div className="text-xs text-gray-500 mt-1">Your testimonial will automatically credit them once approved.</div>
            </div>
          </div>
        )}

        <h1 className="font-display text-3xl text-[#1A1A1A] mb-2" data-testid="testimonial-form-title">Share Your Experience</h1>
        <p className="text-gray-500 mb-10">Tell us about your experience working with Magnus Kor. Your testimonial helps other founders and talent make informed decisions.</p>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="testimonial-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Your Name *</label>
              <input required value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white" data-testid="testimonial-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Email *</label>
              <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white" data-testid="testimonial-email" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Company Name *</label>
              <input required value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white" data-testid="testimonial-company" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Your Role *</label>
              <input required value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white" data-testid="testimonial-role" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Relationship</label>
              <select value={form.relationship_type} onChange={e => setForm({...form, relationship_type: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white" data-testid="testimonial-relationship">
                <option>Client</option>
                <option>Candidate</option>
                <option>Partner</option>
                <option>Contractor</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">LinkedIn URL</label>
              <input value={form.linkedin_url} onChange={e => setForm({...form, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/..."
                className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white" data-testid="testimonial-linkedin" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">Company Logo</label>
            <input type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={e => setLogoFile(e.target.files[0])}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:border file:border-gray-200 file:text-sm file:bg-white file:text-[#1A1A1A] hover:file:bg-gray-50" data-testid="testimonial-logo" />
          </div>

          <div>
            <label className="text-sm font-medium text-[#1A1A1A] block mb-2">Rating *</label>
            <div className="flex gap-1" data-testid="testimonial-rating">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} type="button" onClick={() => setForm({...form, rating: n})}
                  className="p-1 hover:scale-110 transition-transform">
                  <Star size={24} className={n <= form.rating ? "text-[#B8860B] fill-[#B8860B]" : "text-gray-300"} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">What challenge were you facing? *</label>
            <textarea required rows={3} value={form.problem} onChange={e => setForm({...form, problem: e.target.value})} placeholder="Describe the hiring challenge or problem you were facing..."
              className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white resize-none" data-testid="testimonial-problem" />
          </div>

          <div>
            <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">How did Magnus Kor help? *</label>
            <textarea required rows={3} value={form.solution} onChange={e => setForm({...form, solution: e.target.value})} placeholder="Describe how Magnus Kor addressed your challenge..."
              className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white resize-none" data-testid="testimonial-solution" />
          </div>

          <div>
            <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">What was the outcome? *</label>
            <textarea required rows={3} value={form.outcome} onChange={e => setForm({...form, outcome: e.target.value})} placeholder="Share the results - hires made, time saved, impact on your team..."
              className="w-full px-3 py-2.5 border border-gray-200 text-sm focus:border-[#B8860B] focus:outline-none bg-white resize-none" data-testid="testimonial-outcome" />
          </div>

          <button type="submit" disabled={submitting}
            className="w-full py-3 bg-[#1A1A1A] text-white text-sm font-medium hover:bg-[#333] transition-colors disabled:opacity-50" data-testid="testimonial-submit-btn">
            {submitting ? "Submitting..." : "Submit Testimonial"}
          </button>

          <p className="text-xs text-gray-400 text-center">Your testimonial will be reviewed before appearing on our website. By submitting, you agree to our <a href="/terms" className="text-[#B8860B] hover:underline">Terms of Service</a> and <a href="/privacy" className="text-[#B8860B] hover:underline">Privacy Policy</a>.</p>
        </form>
      </div>
    </div>
  );
};

export default TestimonialSubmitPage;
